import React, { useState, useEffect } from 'react';

const CommandModal = ({ command, userTier, isOpen, onClose }) => {
  const [copiedExample, setCopiedExample] = useState(null);

  // Handle Escape key
  useEffect(() => {
    if (!isOpen) return;
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleEscape, true);
    return () => window.removeEventListener('keydown', handleEscape, true);
  }, [isOpen, onClose]);

  if (!isOpen || !command) return null;

  const getTierBadgeColor = (tier) => {
    const tierLower = (tier || 'free').toLowerCase();
    switch (tierLower) {
      case 'free':
        return '#10b981';
      case 'pro':
        return '#3b82f6';
      case 'team':
        return '#8b5cf6';
      case 'enterprise':
        return '#f59e0b';
      default:
        return '#6b7280';
    }
  };

  const handleCopyExample = (example, index) => {
    navigator.clipboard.writeText(example).then(() => {
      setCopiedExample(index);
      setTimeout(() => setCopiedExample(null), 2000);
    });
  };

  const isLocked = () => {
    const tierHierarchy = { free: 0, pro: 1, team: 2, enterprise: 3 };
    const userTierLevel = tierHierarchy[(userTier || 'free').toLowerCase()] ?? 0;
    const cmdTier = (command.tier || 'free').toLowerCase();
    const cmdTierLevel = tierHierarchy[cmdTier] ?? 0;
    return cmdTierLevel > userTierLevel;
  };

  return (
    <div className="baiql-command-modal-overlay" onClick={onClose}>
      <div
        className="baiql-command-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="baiql-command-modal-header">
          <div className="baiql-command-modal-title">
            <code className="baiql-command-modal-name">{command.name}</code>
            {isLocked() && <span className="baiql-command-modal-locked">🔒</span>}
            <span
              className="baiql-command-modal-tier-badge"
              style={{ backgroundColor: getTierBadgeColor(command.tier) }}
            >
              {(command.tier || 'free').toUpperCase()}
            </span>
          </div>
          <button
            className="baiql-command-modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="baiql-command-modal-content">
          {/* Syntax */}
          <div className="baiql-command-modal-section">
            <h3>Syntax</h3>
            <pre className="baiql-command-modal-syntax">
              <code>{command.syntax}</code>
            </pre>
          </div>

          {/* Description */}
          {command.fullDocs && (
            <div className="baiql-command-modal-section">
              <h3>Description</h3>
              <div className="baiql-command-modal-docs">
                {command.fullDocs.split('\n').map((line, i) => (
                  <p key={i}>{line || '\u00A0'}</p>
                ))}
              </div>
            </div>
          )}

          {/* Examples */}
          {command.examples && command.examples.length > 0 && (
            <div className="baiql-command-modal-section">
              <h3>Examples</h3>
              <div className="baiql-command-modal-examples">
                {command.examples.map((example, index) => (
                  <div key={index} className="baiql-command-modal-example">
                    <pre className="baiql-command-modal-example-code">
                      <code>{example}</code>
                    </pre>
                    <button
                      className={`baiql-command-modal-example-copy ${copiedExample === index ? 'copied' : ''}`}
                      onClick={() => handleCopyExample(example, index)}
                      title="Copy example"
                    >
                      {copiedExample === index ? '✓ Copied!' : 'Copy'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Locked message */}
          {isLocked() && (
            <div className="baiql-command-modal-locked-message">
              <p>
                <strong>This command requires {(command.tier || 'pro').toUpperCase()} tier.</strong>
              </p>
              <p>Upgrade your account to access this command.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommandModal;

